import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import SectionHeading from "@/components/SectionHeading";
import { fetchAssets } from "@/lib/api";

gsap.registerPlugin(ScrollTrigger);

const ACTIVICA = '"FT Activica Normal", "Outfit", sans-serif';
const DIN_ALT =
    '"DIN Alternate", "DIN Alternate Bold", "Figtree", "Inter", system-ui, sans-serif';
const DIN = '"Figtree", "Inter", system-ui, sans-serif';
const CARD_GRADIENT =
    "linear-gradient(0deg, #1B1B1B -22.66%, #858585 459.57%)";

// Icons in order, per user request. URLs are public artifact CDN paths.
const ICON_1 =
    "https://customer-assets.emergentagent.com/job_vibecity-lead/artifacts/d4hlcjiz_Frame%202147225533.svg";
const ICON_2 =
    "https://customer-assets.emergentagent.com/job_vibecity-lead/artifacts/esgvgnsk_Frame%202147225539.svg";
const ICON_3 =
    "https://customer-assets.emergentagent.com/job_vibecity-lead/artifacts/z1yyu248_Frame%202147225535.svg";
const ICON_4 =
    "https://customer-assets.emergentagent.com/job_vibecity-lead/artifacts/ab19bpt5_Frame%202147225536.svg";

const PERKS = [
    {
        title: "Official\nCity Lead",
        body: "You'll be publicly announced as the Vibecity Lead for your city.",
        accent: "#05C4E4",
        bg: "#242424",
        iconKey: "perk-icon-1",
        icon: ICON_1,
        iconSize: 110,
    },
    {
        title: "Emergent Backed\nEvents",
        body: "$500 for every approved event you run with quality.",
        accent: "#DE7237",
        bg: CARD_GRADIENT,
        iconKey: "perk-icon-2",
        icon: ICON_2,
        iconSize: 132,
    },
    {
        title: "Direct\nAccess",
        body: "Meet and learn from Emergent's leadership, team, and ecosystem.",
        accent: "#D2FE55",
        bg: CARD_GRADIENT,
        iconKey: "perk-icon-3",
        icon: ICON_3,
        iconSize: 131,
    },
    {
        title: "Leaderboard\nBonus",
        body: "The best-performing city each month gets a $1,000 bonus.",
        accent: "#05C4E4",
        bg: CARD_GRADIENT,
        iconKey: "perk-icon-4",
        icon: ICON_4,
        iconSize: 153,
    },
    {
        title: "Your Story,\nSpotlighted",
        body: "Strong leads get featured on socials & founder-facing content.",
        accent: "#DE7237",
        bg: CARD_GRADIENT,
        iconKey: "perk-icon-1",
        icon: ICON_1,
        iconSize: 141,
    },
];

export default function Rewards() {
    const wrap = useRef(null);
    const [iconMap, setIconMap] = useState({});

    // Hydrate icon URLs from the Assets API (admin-managed image bank).
    // Falls back to the hard-coded constants if the call fails.
    useEffect(() => {
        let cancelled = false;
        fetchAssets()
            .then((arr) => {
                if (cancelled || !Array.isArray(arr)) return;
                const map = {};
                for (const a of arr) {
                    if (a.key && a.image_url) map[a.key] = a.image_url;
                }
                setIconMap(map);
            })
            .catch(() => {
                /* keep constants */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const perks = PERKS.map((p) => ({ ...p, icon: iconMap[p.iconKey] || p.icon }));

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".reward-reveal").forEach((el) => {
                gsap.fromTo(
                    el,
                    { y: 50, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 0.9,
                        ease: "power3.out",
                        scrollTrigger: { trigger: el, start: "top 88%" },
                    },
                );
            });
        }, wrap);
        return () => ctx.revert();
    }, []);

    return (
        <section
            id="perks"
            data-testid="rewards-section"
            ref={wrap}
            className="relative py-24 md:py-32 border-t border-line bg-background overflow-hidden"
        >
            <div className="px-5 md:px-10 lg:px-14 relative max-w-[1640px] mx-auto">
                <SectionHeading
                    lineA="We've Got"
                    lineB="Your Back"
                    accent="blue"
                    testId="rewards-heading"
                    className="reward-reveal"
                    subtitle="Access. Reward. Recognition."
                />

                {/* 5 perk cards - draggable on touch, auto-scrolling on desktop */}
                <div
                    data-testid="rewards-perks"
                    className="reward-reveal -mx-5 md:-mx-10 lg:-mx-14 mt-10 md:mt-12 vc-perks-scroller"
                    style={{
                        WebkitOverflowScrolling: "touch",
                        scrollSnapType: "x mandatory",
                        maskImage:
                            "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                        WebkitMaskImage:
                            "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                    }}
                >
                    <div className="vc-perk-marquee">
                        {[...perks, ...perks].map((p, i) => (
                            <div
                                key={i}
                                className="shrink-0"
                                style={{
                                    width: 302,
                                    scrollSnapAlign: "center",
                                }}
                            >
                                <PerkCard
                                    index={i % perks.length}
                                    perk={p}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function PerkCard({ index, perk }) {
    const { title, body, accent, bg, icon, iconSize } = perk;
    return (
        <div
            data-testid={`perk-${index}`}
            className="relative flex flex-col items-center justify-end w-full"
            style={{
                maxWidth: 302,
                aspectRatio: "302 / 418",
                padding: 20,
                background: bg,
                borderRadius: 20,
                boxShadow: `inset 0 4px 0 ${accent}`,
                gap: 32,
            }}
        >
            {/* Icon */}
            <div
                className="flex-1 w-full grid place-items-center"
                aria-hidden
            >
                <img
                    src={icon}
                    alt=""
                    loading="lazy"
                    style={{
                        width: iconSize,
                        height: iconSize,
                        maxWidth: "60%",
                        maxHeight: "55%",
                        objectFit: "contain",
                    }}
                />
            </div>

            {/* Title + body */}
            <div className="flex flex-col items-center w-full" style={{ gap: 16 }}>
                <h4
                    style={{
                        fontFamily: DIN_ALT,
                        fontWeight: 700,
                        fontSize: "clamp(28px, 2.3vw, 40.7px)",
                        lineHeight: 1.08,
                        textAlign: "center",
                        color: "#FFFCE1",
                        margin: 0,
                        whiteSpace: "pre-line",
                        letterSpacing: "-0.01em",
                    }}
                >
                    {title}
                </h4>
                <p
                    style={{
                        fontFamily: DIN_ALT,
                        fontWeight: 700,
                        fontSize: "clamp(14px, 1.05vw, 18px)",
                        lineHeight: 1.25,
                        textAlign: "center",
                        color: "#646464",
                        margin: 0,
                    }}
                >
                    {body}
                </p>
            </div>
        </div>
    );
}
